'use client'

import { useEffect, useState } from 'react'
import { get } from '@/services/api/axiosClient'

enum ENV_VARIABLES {
    GTM_KEY = 'GTM_KEY',
}

interface EnvVariables {
    [ENV_VARIABLES.GTM_KEY]: string
}

let cachedEnvironmentVariables: EnvVariables | null = null
let environmentVariablesRequest: Promise<EnvVariables | null> | null = null

const loadEnvironmentVariables = async (): Promise<EnvVariables | null> => {
    if (cachedEnvironmentVariables) {
        return cachedEnvironmentVariables
    }

    if (!environmentVariablesRequest) {
        environmentVariablesRequest = get(`api/var`)
            .then((response) => {
                if (ENV_VARIABLES.GTM_KEY in response?.data) {
                    cachedEnvironmentVariables = {
                        [ENV_VARIABLES.GTM_KEY]: response.data[ENV_VARIABLES.GTM_KEY],
                    }
                    return cachedEnvironmentVariables
                }

                return null
            })
            .catch(() => null)
    }

    return environmentVariablesRequest
}

export const useEnvironmentVariables = () => {
    const [env, setEnv] = useState<EnvVariables | null>(cachedEnvironmentVariables)
    const [isLoading, setIsLoading] = useState<boolean>(!cachedEnvironmentVariables)
    const [hasError, setHasError] = useState<boolean>(false)

    const fetchEnvironmentVariables = async () => {
        setIsLoading(true)

        const environmentVariables = await loadEnvironmentVariables()

        if (environmentVariables) {
            setEnv(environmentVariables)
            setHasError(false)
        } else {
            setHasError(true)
        }

        setIsLoading(false)
    }

    useEffect(() => {
        fetchEnvironmentVariables()
    }, [])

    return {
        isLoading,
        fetchEnvironmentVariables,
        hasError,
        env,
    }
}
